import { message } from "antd";
import axios, { AxiosError } from "axios";
import { Dispatch, SetStateAction } from "react";
import { getHistoricalTSDataApi, getMachineDocumentsApi } from "../axios/portalData";
import { MachineDocument, MachinesByGroup, NewGroupDocument, GroupDocument } from "../Types/dashboardTypes";
import { HistoricalTsiDataType } from "../Types/portalTypes";

// This returns a string date with the current date minus the input value. This returns the
// same string format as the historical data api for the oeeDate value
export const getSubtractedDateStringValue = (subtractedDays: number): string => {
  const now = new Date();
  const subtractedDate = new Date(now.getFullYear(), now.getMonth(), now.getDate() - subtractedDays);
  const dd = String(subtractedDate.getDate()).padStart(2, "0");
  const mm = String(subtractedDate.getMonth() + 1).padStart(2, "0"); //January is 0!
  const yyyy = subtractedDate.getFullYear();
  const stringDate = yyyy + "-" + mm + "-" + dd;

  return stringDate;
};

// this calcs the historical avg oee for a selected machine in the machine card
export const getMachineAvgOeeHistorical = (
  machinesByGroup: MachinesByGroup[],
  currentMachineIndex: number,
  groupHistoricalData: HistoricalTsiDataType
): number | null => {
  // make sure that groupHistoricalData has data and the machine exists in machinesByGroup
  if (machinesByGroup[currentMachineIndex] && Object.keys(groupHistoricalData).length > 0) {
    // make sure the group document has changed properly when the group changes
    if (groupHistoricalData[machinesByGroup[currentMachineIndex].machineSerial]) {
      let count = 0;
      let sumOee = 0;
      groupHistoricalData[machinesByGroup[currentMachineIndex].machineSerial].map((value) => {
        if (value.oee) {
          count += 1;
          sumOee += value.oee;
        }
      });
      return Math.round((sumOee / count) * 100);
    } else {
      return null;
    }
  }
  return null;
};

export const getGroupAndMachineDocuments = (
  setGroupDocument: Dispatch<SetStateAction<GroupDocument>>,
  setMachineDocuments: Dispatch<SetStateAction<MachineDocument[] | undefined>>,
  setGroupsMachinesReFetchFlag: Dispatch<SetStateAction<boolean>>
): void => {
  const apiCounter = 1;
  const retry = (apiCounter: number) => {
    getMachineDocumentsApi()
      .then((res) => {
        const machineDocs: MachineDocument[] = [];
        res.map((value) => {
          if (value.documentType == "group") {
            value = value as NewGroupDocument;
            // remove the "_" items
            const newGroupDocument = {
              b2c_oid: value.b2c_oid,
              documentType: value.documentType,
              groups: value.groups,
              id: value.id,
            };
            // re-orders the names so "None" is always at the top, otherwise it is alphabetical
            const groupNames = Object.keys(value.groups);
            for (let i = 0; i < groupNames.length; i++) {
              if (groupNames[i] == "None") {
                groupNames.splice(i, 1);
              }
            }
            groupNames.unshift("None");
            setGroupDocument({
              groupDocument: newGroupDocument,
              groupNames: groupNames,
            });
          } else if (value.documentType == "machine") {
            value = value as MachineDocument;
            // remove the "_" items
            const newMachineDocument = {
              b2c_oid: value.b2c_oid,
              documentType: value.documentType,
              creationDate: value.creationDate,
              id: value.id,
              machineName: value.machineName,
              serialNumber: value.serialNumber,
            };
            machineDocs.push(newMachineDocument);
          }
        });
        setMachineDocuments(machineDocs);
        setGroupsMachinesReFetchFlag(false);
      })
      .catch((error: Error | AxiosError) => {
        console.log("Counter: " + apiCounter);
        if (apiCounter < 3) {
          apiCounter += 1;
          retry(apiCounter);
        } else {
          if (axios.isAxiosError(error)) {
            console.log("AxiosError message: ", error);
            message.error(error.message);
          } else {
            console.log("unexpected error: ", error);
            message.error("An unexpected error occurred");
          }
        }
      });
  };
  retry(apiCounter);
};

// download report button
interface DownloadFile {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any;
  fileName: string;
  fileType: string;
}
export const downloadFile = ({ data, fileName, fileType }: DownloadFile): void => {
  const blob = new Blob([data], { type: fileType });
  const a = document.createElement("a");
  a.download = fileName;
  a.href = window.URL.createObjectURL(blob);
  const clickEvt = new MouseEvent("click", {
    view: window,
    bubbles: true,
    cancelable: true,
  });
  a.dispatchEvent(clickEvt);
  a.remove();
};
export const downloadReportClicked = (
  selectedGroup: string | null,
  machineDocuments: MachineDocument[] | undefined,
  groupDocument: GroupDocument
): void => {
  // get machineId's for the api call below, need machines to map serials to names easily
  const machineIds: string[] = [];
  const machines: { [key: string]: string } = {};
  if (machineDocuments && machineDocuments.length > 0 && groupDocument.groupNames.length > 0) {
    machineDocuments.map((value: MachineDocument) => {
      if (groupDocument.groupDocument.groups[selectedGroup as string].includes(value.serialNumber)) {
        machines[value.serialNumber] = value.machineName;
        machineIds.push(value.serialNumber);
      }
    });
  }

  // get the historical data
  if (machineDocuments && machineDocuments.length > 0) {
    const apiCounter = 1;
    const retry = (machineIds: string[], apiCounter: number) => {
      getHistoricalTSDataApi(machineIds)
        .then((res) => {
          const historicalData = res;
          const jsonData = JSON.stringify(historicalData);
          // json download
          downloadFile({
            data: jsonData,
            fileName: "historicalData.json",
            fileType: "text/json",
          });

          // csv conversion
          // create header using names instead of serials
          let csv = "Dates," + selectedGroup + ",";
          const serialHeader = Object.keys(historicalData);
          const machineNamesHeader: string[] = [];
          serialHeader.map((value) => {
            machineNamesHeader.push(machines[value]);
          });
          csv += machineNamesHeader.join(",") + "\n";

          // get the dates column
          const dates: string[] = [];
          historicalData[serialHeader[0]].map((data) => {
            dates.push(data.date);
          });

          // create the rows for each date
          dates.forEach((date) => {
            const row: (string | number | null)[] = [];
            let dateOee = 0;
            let count = 0;
            serialHeader.forEach((headerValue) => {
              historicalData[headerValue].map((data) => {
                if (data.date == date) {
                  row.push(data.oee);
                  dateOee += data.oee ? data.oee : 0;
                  count += data.oee ? 1 : 0;
                }
              });
            });
            const avgRowOee = count > 0 ? dateOee / count : "";
            csv += date + "," + avgRowOee + ",";
            csv += row.join(",") + "\n";
          });

          // download the csv file
          downloadFile({
            data: csv,
            fileName: "historicalData.csv",
            fileType: "text/csv",
          });
        })
        .catch((error: Error | AxiosError) => {
          console.log("Counter: " + apiCounter);
          if (apiCounter < 3) {
            apiCounter += 1;
            retry(machineIds, apiCounter);
          } else {
            if (axios.isAxiosError(error)) {
              console.log("AxiosError message: ", error);
              message.error(error.message);
            } else {
              console.log("unexpected error: ", error);
              message.error("An unexpected error occurred");
            }
          }
        });
    };
    retry(machineIds, apiCounter);
  }
};
